import {ErrorMessage} from 'formik';
import _ from 'lodash';
import React, {useEffect, useState} from 'react';
import {useIMask} from 'react-imask';
import {FormErrorMessage} from '../../../form-features/FormErrorMessage';
import {InputStructure} from './InputStructure';

/**
 * @param {Object} param0 props
 * @param {boolean} [param0.nullable=false]
 * When `true`, the following unmasked values will be coerced to `null` - empty strings, `null`, `undefined`.
 * Otherwise, the listed unmasked values will be coerced to the default value of the mask, eg. `0` for a number mask.
 *
 * @param {"typed" | "masked"} [param0.valueType]
 * How to store and read the value in the form. If "masked", the value stored in the form will be the same as it's displayed
 *  in the input. If "typed", the typed value is stored in the form.
 * The default value is "masked" if {@link mask} is set, otherwise it's "typed".
 */
export function RawMaskedInput({
    name,
    disabled,
    label,
    prefix,
    suffix,
    form,
    fractionDigits = 2,
    allowNegative = false,
    nullable = false,
    mask,
    lazy = true,
    min,
    max,
    valueType,
}) {
    valueType ??= _.isNil(mask) ? 'typed' : 'masked';
    mask ??= Number;

    const [opts] = useState({
        mask: mask, // other options are optional with defaults below
        scale: fractionDigits, // digits after point, 0 for integers
        signed: allowNegative, // disallow negative
        thousandsSeparator: '.', // any single char
        radix: ',', // fractional delimiter
        mapToRadix: ['.'], // symbols to process as radix
        min: min,
        max: max,
        lazy: lazy,

        /**
         * This function is invoked _after_ iMask formats the number to a string.
         * Here we get the option to do some post-processing.
         * @param {string | Number} displayValue The value as formatted by iMask. Apparently this is sometimes a number, which breaks our component
         * @param {{value: string}} mask The iMask object
         * @returns The string that will be displayed in the field
         */
        format: (displayValue, mask) => {
            // overrides the default behavior of iMask which displays a 0
            // when the underlying value is null or undefined;
            // instead we want to display an empty field
            return mask.value === '' ? '' : '' + displayValue;
        },
    });

    const {ref, setTypedValue, setUnmaskedValue, setValue, maskRef} = useIMask(opts, {
        onAccept: (_, mask) => {
            const normalizedValue =
                nullable && ['', null, undefined].includes(mask.unmaskedValue)
                    ? null
                    : valueType === 'unmasked'
                    ? mask.value
                    : mask.typedValue;

            form.setFieldValue(name, normalizedValue);
        },
    });

    const value = _.get(form.values, name);

    useEffect(() => {
        // update the value when the form value changes.

        // Lord have mercy on my soul. MaskedInput is bad in more ways than one,
        //  but this code makes it work, at least in the parts that I tested.

        // Null values aren't accepted by these methods, so we have to use empty strings.
        const newValue = _.isNil(value) ? '' : value;

        if (valueType === 'masked') {
            setValue(newValue);
        } else {
            if (newValue === '') {
                // not using setTypedValue() for empty strings because it causes weird behavior.
                setUnmaskedValue('');
            } else {
                setTypedValue(newValue);
                setUnmaskedValue(newValue.toString());
            }
        }
    }, [maskRef, name, setTypedValue, setUnmaskedValue, setValue, value, valueType]);

    return (
        <InputStructure
            label={label}
            prefix={prefix}
            suffix={suffix}
            disabled={disabled}
            errorMessage={<ErrorMessage name={name} component={FormErrorMessage}></ErrorMessage>}>
            <input
                ref={ref}
                disabled={disabled}
                type="text"
                id={name}
                defaultValue={_.get(form.values, name)?.toString().replace('.', ',')}
                name={name}
            />
        </InputStructure>
    );
}
